import { useCallback, useMemo, useRef } from 'react';
import {
  useNavigation,
  useRoute,
  useFocusEffect,
  CommonActions,
} from '@react-navigation/native';
import { BackHandler, Platform } from 'react-native';
import type { AppNavigationProp } from '../navigation/types';
import {
  navigationConfig as appNavigationConfig,
  shouldShowBackButton as configShouldShowBackButton,
} from '../config/navigationConfig';
import { logger } from '../utils/logger';

// Navigation configuration constants
const defaultNavigationConfig = {
  mainTabRoutes: appNavigationConfig.mainTabRoutes,
  authRoutes: [
    'Login',
    'Register',
    'ForgotPassword',
    'ResetPassword',
    'VerifyEmail',
  ],
  drawerFeatureRoutes: appNavigationConfig.drawerFeatureRoutes,
} as const;

const navigationPatterns = {
  stack: {
    showBackButton: (_routeName: string, canGoBack: boolean) => canGoBack,
  },
  tab: {
    showBackButton: (
      routeName: string,
      canGoBack: boolean,
      forceShow?: boolean
    ) =>
      forceShow ||
      (canGoBack &&
        !defaultNavigationConfig.mainTabRoutes.includes(routeName as any)),
  },
  modal: {
    showBackButton: () => true,
  },
} as const;

export interface NavigationState {
  canGoBack: boolean;
  backAction: () => void;
  showBackButton: boolean;
  backButtonLabel: string;
  routeName: string;
  routeKey: string;
  isTabScreen: boolean;
  isAuthScreen: boolean;
}

export interface UseMobileNavigationOptions {
  customBackAction?: () => void | Promise<void>;
  backButtonLabel?: string;
  forceShowBack?: boolean;
  hideBackButton?: boolean;
  pattern?: keyof typeof navigationPatterns;
  onBackPressed?: () => boolean; // Return true to prevent default back action
  enableHardwareBackButton?: boolean; // Android hardware back button support
}

/**
 * Enhanced mobile navigation hook with optimized performance and flexibility
 * Supports React Navigation 6+ with TypeScript and performance optimizations
 */
export function useMobileNavigation(
  options: UseMobileNavigationOptions = {}
): NavigationState {
  const navigation = useNavigation<AppNavigationProp>();
  const route = useRoute();

  const {
    customBackAction: _customBackAction,
    backButtonLabel = 'Back',
    forceShowBack = false,
    hideBackButton = false,
    pattern = 'tab',
    onBackPressed: _onBackPressed,
    enableHardwareBackButton = Platform.OS === 'android',
  } = options;

  // Stable references for performance
  const optionsRef = useRef(options);
  optionsRef.current = options;

  // Check if we're inside a drawer feature route (journey, aiExperience)
  const isInDrawerFeatureRoute = useMemo(() => {
    // Check parent navigation states to see if we're nested inside journey or aiExperience
    let parent: any = (navigation as any).getParent?.();
    let depth = 0;
    while (parent && depth < 5) {
      const state: any = parent.getState?.();
      const currentRoute = state?.routes?.[state?.index];
      if (
        defaultNavigationConfig.drawerFeatureRoutes.includes(
          currentRoute?.name as any
        )
      ) {
        return true;
      }
      parent = parent.getParent?.();
      depth += 1;
    }
    return false;
  }, [navigation]);

  // Memoized route information
  const routeInfo = useMemo(
    () => ({
      name: route.name,
      key: route.key,
      isTabScreen: defaultNavigationConfig.mainTabRoutes.includes(
        route.name as any
      ),
      isAuthScreen: defaultNavigationConfig.authRoutes.includes(
        route.name as any
      ),
      isInDrawerFeatureRoute: isInDrawerFeatureRoute,
      // Use config to determine if this is a child route
      isChildRoute: configShouldShowBackButton(route.name),
    }),
    [route.name, route.key, isInDrawerFeatureRoute]
  );

  // Stable canGoBack reference
  const canGoBack = useMemo(() => {
    try {
      return navigation.canGoBack();
    } catch (error) {
      logger.warn('Navigation canGoBack failed:', error);
      return false;
    }
  }, [navigation]);

  // Helper to extract parent route from URL path or route structure
  const getParentRoute = useCallback(() => {
    try {
      // Strategy 1: Check navigation state hierarchy
      const state = navigation.getState();

      // Look for parent in navigation stack
      if (state.routes.length > 1) {
        const parentRoute = state.routes[state.routes.length - 2];
        if (
          parentRoute &&
          defaultNavigationConfig.mainTabRoutes.includes(
            parentRoute.name as any
          )
        ) {
          return parentRoute.name;
        }
      }

      // Strategy 2: Check if route has a path property and parse it
      if (route.path) {
        const pathSegments = route.path.split('/').filter(Boolean);
        if (pathSegments.length > 0) {
          return pathSegments[0];
        }
      }

      // Strategy 3: Infer parent from route name patterns
      // e.g., "RecipeDetail" -> "recipes", "CreatorProfile" -> "creators"
      const routeName = route.name.toLowerCase();
      for (const tabRoute of defaultNavigationConfig.mainTabRoutes) {
        if (routeName.includes(tabRoute.toLowerCase())) {
          return tabRoute;
        }
      }

      return null;
    } catch (error) {
      logger.warn('Failed to determine parent route:', error);
      return null;
    }
  }, [navigation, route.name, route.path]);

  // Navigate to a specific tab route
  const navigateToTab = useCallback(
    (tabName: string) => {
      navigation.dispatch(
        CommonActions.reset({
          index: 0,
          routes: [{ name: 'home', state: { routes: [{ name: tabName }] } }],
        })
      );
    },
    [navigation]
  );

  // Back action using strategy pattern for clean separation of concerns
  const backAction = useCallback(async () => {
    // Strategy 1: Custom handler intercept
    if (optionsRef.current.onBackPressed?.()) {
      return;
    }

    // Strategy 2: Custom back action
    if (optionsRef.current.customBackAction) {
      try {
        await optionsRef.current.customBackAction();
      } catch (error) {
        logger.error('Custom back action failed:', error);
      }
      return;
    }

    // Strategy 3: Native navigation stack
    if (canGoBack) {
      navigation.goBack();
      return;
    }

    // Strategy 4: Parent route inference (for deep links / direct URL access)
    const parentRoute = getParentRoute();
    navigateToTab(parentRoute ?? 'recipes');
  }, [canGoBack, navigation, getParentRoute, navigateToTab]);

  // Hardware back button handler (Android)
  useFocusEffect(
    useCallback(() => {
      if (!enableHardwareBackButton) return;

      const onBackPress = () => {
        // Use the same logic as our back action
        backAction();
        return true; // Prevent default behavior
      };

      const subscription = BackHandler.addEventListener(
        'hardwareBackPress',
        onBackPress
      );
      return () => subscription.remove();
    }, [backAction, enableHardwareBackButton])
  );

  // Determine if back button should be shown based on pattern
  const showBackButton = useMemo(() => {
    if (hideBackButton) return false;

    // NEVER show back button for main tab screens, regardless of navigation state
    if (routeInfo.isTabScreen) return false;

    // Use config-based logic for child routes
    if (routeInfo.isChildRoute) return true;

    // For routes inside drawer features (journey, aiExperience),
    // show back button only for child screens (not index)
    if (routeInfo.isInDrawerFeatureRoute && routeInfo.name !== 'index') {
      return true;
    }

    const patternConfig = navigationPatterns[pattern];

    switch (pattern) {
      case 'stack':
        return patternConfig.showBackButton(routeInfo.name, canGoBack);
      case 'tab':
        return patternConfig.showBackButton(
          routeInfo.name,
          canGoBack,
          forceShowBack
        );
      case 'modal':
        return patternConfig.showBackButton(routeInfo.name, canGoBack);
      default:
        return forceShowBack || canGoBack;
    }
  }, [
    hideBackButton,
    pattern,
    routeInfo.name,
    canGoBack,
    forceShowBack,
    routeInfo.isTabScreen,
    routeInfo.isChildRoute,
    routeInfo.isInDrawerFeatureRoute,
  ]);

  return {
    canGoBack,
    backAction,
    showBackButton,
    backButtonLabel,
    routeName: routeInfo.name,
    routeKey: routeInfo.key,
    isTabScreen: routeInfo.isTabScreen,
    isAuthScreen: routeInfo.isAuthScreen,
  };
}
